import React from "react";
import { 
  Container, 
  Typography, 
  Button, 
  Box, 
  Paper,
  Alert,
  AlertTitle,
  Divider,
  Link
} from "@mui/material";
import SecurityIcon from '@mui/icons-material/Security';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InfoIcon from '@mui/icons-material/Info';

interface AccessDeniedProps {
  onTryDifferentAccount?: () => void; 
  tenantId?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  onTryDifferentAccount,
  tenantId 
}) => {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          textAlign: "center",
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        {/* Header with Icon */}
        <Box sx={{ mb: 3 }}>
          <SecurityIcon 
            sx={{ 
              fontSize: 64, 
              color: 'warning.main',
              mb: 2
            }} 
          />
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1, 
              color: "text.primary",
              fontWeight: 600
            }}
          >
            Access Not Permitted
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            sx={{ fontSize: '1.1rem' }}
          >
            Your organization needs to be authorized to use AppConfig²
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Main Explanation */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mb: 3, 
            textAlign: 'left',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Why am I seeing this message?
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            AppConfig² uses a secure allowlist system to control access. Your organization's 
            tenant needs to be pre-authorized before you can use the application.
          </Typography>
          
          {tenantId && (
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              bgcolor: 'rgba(0,0,0,0.04)', 
              borderRadius: 1,
              fontFamily: 'monospace'
            }}>
              <Typography variant="caption" color="text.secondary">
                Your Tenant ID:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                {tenantId}
              </Typography>
            </Box>
          )}
        </Alert>

        {/* Action Steps */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
            How to Get Access
          </Typography>
          
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Option 1:</strong> Contact your IT administrator and ask them to:
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Request access to AppConfig² for your organization
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Provide your tenant ID (shown above) if available
              </Typography>
              <Typography variant="body2">
                • Submit a tenant allowlist request
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Option 2:</strong> Request access directly:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2">
                Contact us at{' '}
                <Link 
                  href="mailto:support@appconfig.app?subject=Tenant Access Request&body=Please add our tenant to the AppConfig² allowlist.%0A%0ATenant ID: [Your Tenant ID]%0AOrganization: [Your Organization Name]%0AContact: [Your Email]"
                  sx={{ fontWeight: 500 }}
                >
                  support@appconfig.app
                </Link>{' '}
                to request tenant authorization
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<ContactSupportIcon />}
            href="mailto:support@appconfig.app?subject=Tenant Access Request"
            sx={{ 
              minWidth: 200,
              borderRadius: 2
            }}
          >
            Request Access
          </Button>
          
          {onTryDifferentAccount && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<SwapHorizIcon />}
              onClick={onTryDifferentAccount}
              sx={{ 
                minWidth: 200,
                borderRadius: 2
              }}
            >
              Try Different Account
            </Button>
          )}
        </Box>

        {/* Additional Info */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Access requests are typically processed within 1-2 business days.
            <br />
            For urgent requests, please mention "Urgent" in your email subject.
          </Typography>
        </Box>

        {/* Footer Link */}
        <Box sx={{ mt: 2 }}>
          <Link 
            href="https://appconfigapp.toncheetah.com" 
            target="_blank" 
            rel="noopener noreferrer"
            variant="body2"
            sx={{ color: 'primary.main' }}
          >
            Learn more about AppConfig²
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default AccessDenied;